<template>
  <fieldset>
    <div class="row">
      <label class="col-sm-3 control-label pt-10"> Pickup Time* </label>
      <div class="col-sm-8">
        <date-picker
          v-model="props.data.pickupDate"
          :disabled-date="disabledDate"
          required
        />
        <time-picker
          v-model="props.data.pickupTime"
          class="mt-5"
          clearable
          format="hh:mm a"
          required
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> Does the rider require a WAV?* </label>
      <div class="col-sm-5">
        <dropdown-list
          v-model="taxiNoteData.isWav"
          :options="yesNoOptions"
          required
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> Is this request 48 hours in advance?* </label>
      <div class="col-sm-5">
        <dropdown-list
          v-model="taxiNoteData.hoursAdvance"
          :options="yesNoOptions"
          required
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> Is this a round trip?* </label>
      <div class="col-sm-5">
        <dropdown-list
          v-model="taxiNoteData.isRoundTrip"
          :options="yesNoOptions"
          required
        />
      </div>
    </div>

    <div
      v-if="taxiNoteData.isRoundTrip === YesNoEnum.Yes"
      class="row"
    >
      <label class="col-sm-3 control-label pt-5"> Return Time* </label>
      <div class="col-sm-8">
        <time-picker
          v-model="data.returnTime"
          required
          clearable
          format="hh:mm a"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> Does the rider have a backup phone number?* </label>
      <div class="col-sm-5">
        <dropdown-list
          v-model="taxiNoteData.hasBackupPhone"
          :options="yesNoOptions"
          required
        />
      </div>
    </div>

    <div
      v-if="taxiNoteData.hasBackupPhone === YesNoEnum.Yes"
      class="row pt-10"
    >
      <label class="col-sm-3 control-label pt-5"> Backup Phone Number* </label>
      <phone-number-input
        v-model="taxiNoteData.backupPhoneNumber"
        required
        class="col-sm-8"
        page-id="LocalTaxiRideData"
      />
    </div>

    <div class="row pt-2">
      <label class="col-sm-3 control-label pt-5"> Note to Taxi Company </label>
      <div class="col-sm-8">
        <sanitized-textarea
          v-model="props.data.noteToTaxiCompany"
          autosize
        />
        <span class="help-block"> This note will be sent to the taxi company via email. </span>
      </div>
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import util from '@/Utils/util';
import { reactive, computed, watch } from 'vue';
import moment from 'moment';

import { yesNoOptions } from '@/constants/app';
import { YesNoEnum } from '@/models/app';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  disabledDate: {
    type: Function,
    default: () => null,
  },
});

const emit = defineEmits(['setLocalTaxiDisabledState']);

const taxiNoteData = reactive({
  isWav: '',
  hoursAdvance: '',
  isRoundTrip: '',
  hasBackupPhone: '',
  backupPhoneNumber: null,
});

// Computed property that constructs the complete note based on the current state.
const computedNote = computed(() => {
  const noteParts: string[] = [];

  if (taxiNoteData.isWav === YesNoEnum.Yes) {
    noteParts.push('Rider requires a WAV.');
  }

  if (taxiNoteData.hoursAdvance === YesNoEnum.Yes) {
    noteParts.push('Request is 24-48 hours in advance.');
  } else if (taxiNoteData.hoursAdvance === YesNoEnum.No) {
    noteParts.push('This request is being submitted with less than 24 hours advance notice.');
  }

  if (taxiNoteData.isRoundTrip === YesNoEnum.Yes && props.data.returnTime) {
    const formattedTime = moment(props.data.returnTime).format('hh:mm A');
    noteParts.push(`This is round trip and return time is ${formattedTime}.`);
  } else if (taxiNoteData.isRoundTrip === YesNoEnum.No) {
    noteParts.push('This is a one-way ride only.');
  }

  if (taxiNoteData.hasBackupPhone === YesNoEnum.Yes && util.isPhoneNumberValid(taxiNoteData.backupPhoneNumber)) {
    noteParts.push(`Rider has a backup phone number and it is ${taxiNoteData.backupPhoneNumber}.`);
  }

  return noteParts.join(' ');
});

// Update the note to taxi company whenever the computed note changes.
watch(computedNote, (newNote) => {
  props.data.noteToTaxiCompany = newNote;
});

watch(
  () => [taxiNoteData.isRoundTrip, props.data.returnTime, taxiNoteData.hasBackupPhone, taxiNoteData.backupPhoneNumber],
  ([isRoundTrip, returnTime, hasBackupPhone, backupPhoneNumber]) => {
    const isRoundTripInvalid = isRoundTrip === YesNoEnum.Yes && !returnTime;
    const isBackupPhoneInvalid = hasBackupPhone === YesNoEnum.Yes && !util.isPhoneNumberValid(backupPhoneNumber);
    const isDisabled = isRoundTripInvalid || isBackupPhoneInvalid;
    emit('setLocalTaxiDisabledState', isDisabled);
  },
  { immediate: true },
);

watch(() => taxiNoteData.isRoundTrip, (value: string) => {
  if (value === YesNoEnum.No) {
    props.data.returnTime = null;
  }
});
</script>
